import React, { useState, useRef, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import { Settings } from 'lucide-react';

const Research = () => {
  const [rotation, setRotation] = useState(0);
  const contentRef = useRef(null);
  const gearRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [lastAngle, setLastAngle] = useState(0);
  const [totalRotation, setTotalRotation] = useState(0);

  // Calculate angle from center of gear
  const calculateAngle = (e) => {
    if (!gearRef.current) return 0;

    const rect = gearRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = e.clientX - centerX;
    const deltaY = e.clientY - centerY;

    return Math.atan2(deltaY, deltaX) * (180 / Math.PI);
  };

  // Handle wheel scroll on the gear
  const handleWheel = (e) => {
    e.preventDefault();
    const scrollAmount = e.deltaY * 0.5;

    if (contentRef.current) {
      const newScrollTop = contentRef.current.scrollTop + scrollAmount;
      contentRef.current.scrollTop = Math.max(0, Math.min(newScrollTop, contentRef.current.scrollHeight - contentRef.current.clientHeight));

      // Rotate gear based on scroll
      const newTotalRotation = totalRotation + (scrollAmount * 0.3);
      setTotalRotation(newTotalRotation);
      setRotation(newTotalRotation);
    }
  };

  // Handle drag on the gear
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const angle = calculateAngle(e);
    setLastAngle(angle);
  };

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;

    const currentAngle = calculateAngle(e);
    let deltaAngle = currentAngle - lastAngle;

    // Handle angle wrap-around
    if (deltaAngle > 180) deltaAngle -= 360;
    if (deltaAngle < -180) deltaAngle += 360;

    // Update total rotation
    const newTotalRotation = totalRotation + deltaAngle;
    setTotalRotation(newTotalRotation);
    setRotation(newTotalRotation);

    // Convert rotation to scroll
    const scrollAmount = deltaAngle * 2;

    if (contentRef.current) {
      const newScrollTop = contentRef.current.scrollTop + scrollAmount;
      contentRef.current.scrollTop = Math.max(0, Math.min(newScrollTop, contentRef.current.scrollHeight - contentRef.current.clientHeight));
    }

    setLastAngle(currentAngle);
  }, [isDragging, lastAngle, totalRotation]);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Handle scroll on the content box
  const handleContentScroll = () => {
    if (contentRef.current && !isDragging) {
      const scrollTop = contentRef.current.scrollTop;
      const newRotation = scrollTop * 0.3;
      setRotation(newRotation);
      setTotalRotation(newRotation);
    }
  };

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, lastAngle, totalRotation, handleMouseMove]);

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden">
      {/* Mobile Navbar */}
      <div className="md:hidden">
        <Navbar dark={true} mobile={true} />
      </div>
      
      {/* Desktop Navbar */}
      <div className="hidden md:block">
        <Navbar dark={true} />
      </div>
      
      <div className="container mx-auto px-8 py-12 h-[calc(100vh-100px)]">
        <div className="flex items-center justify-center h-full gap-8">
          {/* Gear Section - hidden on mobile */}
          <div className="hidden md:flex flex-col items-center">
            <p className="text-sm mb-4 text-gray-400">rotate the wheel<br/>to see more</p>
            <div
              ref={gearRef}
              className="cursor-grab active:cursor-grabbing select-none relative"
              onWheel={handleWheel}
              onMouseDown={handleMouseDown}
              style={{ transform: `rotate(${rotation}deg)` }}
            >
              <Settings size={120} className="text-gray-500 transition-transform duration-100" strokeWidth={1.5} />
              {/* Visual indicator for rotation direction */}
              {/* <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-2 h-2 bg-blue-500 rounded-full absolute" style={{ top: '10px' }}></div>
              </div> */}
            </div>
          </div>
          
          {/* Content Box */}
          <div className="relative w-full max-w-3xl h-[600px]">
            <div
              ref={contentRef}
              onScroll={handleContentScroll}
              className="absolute inset-0 overflow-y-auto scrollbar-hide bg-gray-900 backdrop-blur-sm border border-blue-500/30 rounded-lg p-8"
              style={{
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitScrollbar: { display: 'none' }
              }}
            >
              <div className="space-y-6">
                <h2 className="text-3xl font-light mb-8">Roya's Research</h2>
                
                <div className="space-y-4">
                  <h3 className="text-2xl font-light mb-8">Background Story</h3>
                  <p className="text-lg font-light">
                    Since I was introduced to computers and networking in high school I was thinking about the ways in which certain features could be exploited or used in a way that they are not supposed to be used. Where can things be useful where they are not usually used? I also kind of became obsessed with automation, I like to script and automate everything (especially when it’s related to creating content in latex). Then I went to university to study computer science and engineering in more depth and in my first year I met this professor who talked passionately about his security research. That was when I first thought about cybersecurity at all, but I would not get involved with it until much later. I also accidentally became a part of a research paper on LLMs in my first year, little did I know that the research is going to last another year and will affect me quite a bit.
                  </p>
                  <h3 className="text-2xl font-light mb-8">Today (2024-2025)</h3>
                  <p className="text-lg font-light">
                    Right now I am focused on continuing the research I have been doing for the entirety of 2024-2025 academic year on security vulnerabilities of language models, specifically Membership Inference Attacks. Together with my invaluable co-author 
                    <a href='https://www.linkedin.com/in/hosein-madadi-a56aaa189/'>Hosein Mamadi Tamar</a> and our fantastic professor <a href='https://mysite.ku.edu.tr/emregursoy/'>Emre Gursoy</a> I have worked tirelessly to reveal vulnerabilities and find new ways to detect threats for language models. Currently I am also working with a young and very talented 
                    <a href='https://www.linkedin.com/in/yusuf-bayram-41702a33b/'>Yusuf Bayram</a> on extending our research into federated learning scenarios, hoping to shed light to the vulnerabilities there.
                  </p>
                  <h3 className="text-2xl font-light mb-8">2024</h3>
                  <p className="text-lg font-light">
                    I finished helping <a href='https://www.linkedin.com/in/ozyurtbatuhan/?originalSubdomain=tr'>Batuhan Ozyurt</a> with our work on spatial perception of language models in different narratives. The work can be found <a href='https://arxiv.org/abs/2504.03434'>here</a>. Batuhan was very patient with me and I actually started helping him in 2023. 
                  </p>
                  <h3 className="text-2xl font-light mb-8">2022-2023</h3>
                  <p className="text-lg font-light">
                    Getting adjusted to life in a new country, starting my academic journey.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`  
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;  /* IE and Edge */
          scrollbar-width: none;  /* Firefox */
        }
        a:hover {
           text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default Research;
